import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import googleDriveService from '../../services/googleDrive';

const DocumentCreationModal = ({ isOpen, onClose, currentFolder, onDocumentCreated }) => {
  const [documentName, setDocumentName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setDocumentName('');
      setError(null);
      // Add body class to prevent scrolling
      document.body.classList.add('modal-open');
    } else {
      // Remove body class to restore scrolling
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const handleCreateDocument = async () => {
    if (!documentName.trim()) {
      setError('Document name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const accessToken = await googleDriveService.getAccessToken();
      
      const fileMetadata = {
        name: documentName.trim(),
        mimeType: 'application/vnd.google-apps.document',
        parents: [currentFolder?.id]
      };

      console.log('Creating document:', fileMetadata);

      const response = await fetch('https://www.googleapis.com/drive/v3/files?supportsAllDrives=true', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(fileMetadata)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Document creation failed:', errorText);
        throw new Error('Failed to create document');
      }

      const document = await response.json();
      console.log('✅ Document created successfully:', document);

      // Add permissions for domain
      try {
        await fetch(`https://www.googleapis.com/drive/v3/files/${document.id}/permissions?supportsAllDrives=true`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            role: 'writer',
            type: 'domain',
            domain: 'drilldocs.com',
            allowFileDiscovery: true
          })
        });
      } catch (err) {
        console.warn('Failed to set permissions:', err);
      }

      onDocumentCreated(document);
      onClose();
      
      // Open the document for editing immediately
      window.open(`https://docs.google.com/document/d/${document.id}/edit`, '_blank');
      
      window.location.reload(); // Refresh to show new document

    } catch (error) {
      console.error('Failed to create document:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCreateDocument();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay">
      <div className="document-creation-modal" style={{ maxWidth: '400px' }}>
        <div className="modal-header">
          <h2>Create Document</h2>
          <button 
            className="modal-close-btn"
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="doc-name">Document Name</label>
            <input
              id="doc-name"
              type="text"
              className="form-input"
              placeholder="Enter document name..."
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={loading}
              autoFocus
            />
          </div>

          {currentFolder && (
            <div className="form-group">
              <label>Location</label>
              <div className="location-info">
                📁 {currentFolder.name}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCreateDocument}
            disabled={loading || !documentName.trim()}
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                Creating...
              </>
            ) : (
              'Create'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default DocumentCreationModal;